// Cadence › Vision (`/c/{slug}/cadence/vision`; 06 §Cadence "Vision page"): the company's vision
// in five sections — core values, focus, long-term target, 3-year picture, 1-year plan — as
// readable text. Managers edit a section in place (saved with the version read: someone else's
// save since is a conflict, said plainly). Under the 1-year plan, the quarter's priorities, each
// linking to Priorities.
import {
  Badge,
  Button,
  buttonVariants,
  LensBandDot,
  LocationHeader,
  Notice,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CompassIcon, PencilIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { VisionView } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { ErrorNotice } from "../structure/parts";
import { cadenceKeys, visionQuery } from "./cadence-queries";

type Key = VisionView["sections"][number]["key"];

const HINTS: Record<Key, string> = {
  core_values: "The few rules everyone hires, fires and decides by.",
  focus: "Why the company exists and what it is best at.",
  long_term_target: "The 10-year target everything points at.",
  three_year_picture: "What the company looks like three years from now.",
  one_year_plan: "This year's goals on the way there.",
};

const TRACK: Record<string, { label: string; band: "green" | "amber" | "red" }> = {
  on: { label: "On track", band: "green" },
  at_risk: { label: "At risk", band: "amber" },
  off: { label: "Off track", band: "red" },
};

export function VisionScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const q = useQuery(visionQuery(api, co));
  const [editing, setEditing] = useState<Key | null>(null);
  const v = q.data;
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="vision">
      <LocationHeader
        icon={CompassIcon}
        title="Vision"
        badge={
          v?.updated_at ? (
            <Badge variant="neutral">
              Updated{" "}
              {new Date(v.updated_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </Badge>
          ) : null
        }
      />
      <div className="min-h-0 flex-1 overflow-auto px-6 pt-6 pb-12 max-md:px-4">
        {q.isPending ? (
          <Loading label="Loading the vision" rows={6} />
        ) : q.isError || !v ? (
          <ErrorNotice what="The vision" error={q.error} />
        ) : (
          <div className="mx-auto grid max-w-3xl gap-4">
            {!v.page_id && !v.can_edit ? (
              <Notice>The vision has not been written yet. Managers write it here.</Notice>
            ) : null}
            {v.sections.map((s) => (
              <Section
                key={s.key}
                api={api}
                co={co}
                view={v}
                section={s}
                editing={editing === s.key}
                onEdit={(on) => setEditing(on ? s.key : null)}
              >
                {s.key === "one_year_plan" ? (
                  <QuarterPriorities company={company} view={v} />
                ) : null}
              </Section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  api,
  co,
  view: v,
  section: s,
  editing,
  onEdit,
  children,
}: {
  api: Api;
  co: string;
  view: VisionView;
  section: VisionView["sections"][number];
  editing: boolean;
  onEdit: (on: boolean) => void;
  children?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const fid = useId();
  const [text, setText] = useState(s.text);
  const save = useMutation({
    mutationFn: () =>
      api.client.vision.update({
        params: { co },
        body: { sections: { [s.key]: text }, version: v.version },
      }),
    onSuccess: (next) => {
      qc.setQueryData(cadenceKeys.vision(co), next);
      onEdit(false);
    },
  });
  const conflict = save.isError && (save.error as { status?: number } | null)?.status === 409;
  return (
    <section
      className="group/row rounded-lg border border-border bg-card p-5"
      data-slot="vision-section"
      data-key={s.key}
      aria-labelledby={`${fid}-h`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h2 id={`${fid}-h`} className="text-base font-semibold text-foreground">
            {s.label}
          </h2>
          <p className="text-xs text-muted-foreground">{HINTS[s.key]}</p>
        </div>
        {v.can_edit && !editing ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Edit ${s.label}`}
            onClick={() => {
              setText(s.text);
              save.reset();
              onEdit(true);
            }}
          >
            <PencilIcon data-icon="inline-start" aria-hidden="true" />
            Edit
          </Button>
        ) : null}
      </div>
      {editing ? (
        <form
          className="mt-3 grid gap-2"
          aria-label={`Edit ${s.label}`}
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label htmlFor={`${fid}-t`} className="sr-only">
            {s.label}
          </label>
          <Textarea
            id={`${fid}-t`}
            rows={6}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {save.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {conflict
                ? "Someone saved the vision since you opened it. Cancel, read their version, and edit again."
                : problemText(save.error, "The vision could not be saved.")}
            </p>
          ) : null}
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onEdit(false);
                if (conflict) void qc.invalidateQueries({ queryKey: cadenceKeys.vision(co) });
              }}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={save.isPending}>
              Save
            </Button>
          </div>
        </form>
      ) : s.text.trim() ? (
        <p className="mt-3 text-sm leading-relaxed whitespace-pre-line text-foreground">{s.text}</p>
      ) : (
        <p className="mt-3 text-sm text-faint">Not written yet.</p>
      )}
      {children}
    </section>
  );
}

function QuarterPriorities({ company, view: v }: { company: CompanyAccess; view: VisionView }) {
  return (
    <div className="mt-4 border-t border-subtle pt-3" data-slot="vision-priorities">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          This quarter's priorities{" "}
          <span className="font-normal text-muted-foreground">
            Q{v.quarter.slice(6)} {v.quarter.slice(0, 4)}
          </span>
        </h3>
        <Link
          to="/c/$slug/kpis/priorities"
          params={{ slug: company.slug }}
          search={keepFocus}
          className={`${buttonVariants({ variant: "ghost", size: "xs" })} ml-auto`}
        >
          Open Priorities
        </Link>
      </div>
      {v.priorities.length ? (
        <ul className="mt-2 grid gap-1">
          {v.priorities.map((p) => {
            const t =
              p.state === "achieved"
                ? { label: "Done", band: "green" as const }
                : (TRACK[p.track] ?? TRACK.on);
            return (
              <li key={p.id} className="flex items-center gap-2 text-sm">
                <LensBandDot band={t?.band ?? "green"} aria-label={t?.label ?? ""} />
                <Link
                  to="/c/$slug/kpis/priorities"
                  params={{ slug: company.slug }}
                  search={(prev: Record<string, unknown>) => ({
                    ...keepFocus(prev),
                    seat: p.seat_id,
                  })}
                  className="min-w-0 truncate text-foreground hover:underline"
                >
                  {p.title}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-faint">No priorities this quarter yet.</p>
      )}
    </div>
  );
}
